/**
 * SecretDetailsModal Component
 *
 * Modal for viewing secret details.
 *
 * SECURITY NOTES:
 * - Secret values are NEVER displayed
 * - Only metadata is shown
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useSecretDetails } from '../hooks/useSecretDetails';
import { formatDate } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';

interface SecretDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  secretId: string;
}

/**
 * SecretDetailsModal component
 */
export const SecretDetailsModal: React.FC<SecretDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  secretId,
}) => {
  const { data: secret, isLoading, error } = useSecretDetails(tenantId, secretId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>Secret Details</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Secret Details */}
        {secret && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Secret Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{secret.secretName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Secret Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={secret.secretCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Secret ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={secret.secretId} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Value</dt>
                  <dd className="mt-1 text-sm text-gray-500 italic">
                    (encrypted, not shown)
                  </dd>
                </div>
                {secret.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{secret.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(secret.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(secret.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                {secret.lastUpdatedBy && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Last Updated By</dt>
                    <dd className="mt-1 text-sm text-gray-900">{secret.lastUpdatedBy}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
